import NiceModal from "@ebay/nice-modal-react";
import { ThemeProvider } from "@frontend/ui-kit/ThemeProvider";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { canvClass } from "@/canv/canv";
import { canv, setCanv } from "@/canv/global";
import type { BaseInfo, InitialData } from "@/canv/types";
import { type EventHandler, eventEmitter } from "@/eventEmitter";
import { BottomBar } from "@/interface/BottomBar";
import { RightSheet } from "@/interface/RightSheet";
import {
    activeVoteVisibilityAtom,
    arrowToolMenuLineType,
    arrowToolMenuVisibility,
    boardInfoAtom,
    buttonDisabledAtom,
    contextMenuVisibilityAtom,
    contextMenuVisibilityItemsAtom,
    currentToolAtom,
    emoteToolMenuAtomActive,
    emoteToolMenuAtomVisibility,
    headerActiveButtonAtom,
    linkInputCoordinatesAtom,
    linkInputDefaultValueAtom,
    linkPopupStateAtom,
    mindMapToolMenuActivePresetAtom,
    mindMapToolMenuVisibilityAtom,
    minimapUpdateAtom,
    moreToolVisibility,
    multiCursorsAtom,
    penToolMenuColorAtom,
    penToolMenuLineWidthAtom,
    penToolMenuTypeAtom,
    penToolMenuVisibilityAtom,
    presentationScreenVisibility,
    presentationUID,
    searchResultAtom,
    startVoteActiveTabAtom,
    startVoteVisibilityAtom,
    stickerToolMenuColor,
    stickerToolMenuVisibility,
    templateToolMenuVisibility,
    timerAtom,
    toolBoxVisibility,
    usersAtom,
    voteTimerVisibilityAtom,
    widgetMenuAvailableOptions,
    widgetMenuCoordinatesAtom,
    widgetMenuOptionsAtom,
    zoomAtom,
} from "./AppStore";
import { ContextMenu } from "./ContextMenu";
import { FullscreenPresentationLayout } from "./FullscreenPresentationLayout";
import { HeaderBar } from "./HeaderBar";
import { LinkInput } from "./LinkInput";
import { LinkPopup } from "./LinkPopup";
import { SideBar } from "./SideBar";
import { WidgetMenu } from "./WidgetMenu";

type AppProps = {
    boardData: BaseInfo;
};

export const App = ({ boardData }: AppProps) => {
    if (!canv) {
        const c = new canvClass();
        setCanv(c);
        c.startApp(boardData);
    }

    const setZoom = useSetAtom(zoomAtom);
    const setCurrentTool = useSetAtom(currentToolAtom);
    const setSearchResults = useSetAtom(searchResultAtom);
    const setBoardInfo = useSetAtom(boardInfoAtom);
    const setMultiCursors = useSetAtom(multiCursorsAtom);
    const setButtonDisabled = useSetAtom(buttonDisabledAtom);
    const setUsers = useSetAtom(usersAtom);
    const setVoteTimerVisibility = useSetAtom(voteTimerVisibilityAtom);
    const setHeaderActiveButtonName = useSetAtom(headerActiveButtonAtom);
    const setStartVoteVisibility = useSetAtom(startVoteVisibilityAtom);
    const setStartVoteActiveTab = useSetAtom(startVoteActiveTabAtom);
    const setActiveVoteVisibility = useSetAtom(activeVoteVisibilityAtom);
    const setWidgetMenuCoordinates = useSetAtom(widgetMenuCoordinatesAtom);
    const setWidgetMenuAvailableOptions = useSetAtom(widgetMenuAvailableOptions);
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);
    const setContextMenuVisibility = useSetAtom(contextMenuVisibilityAtom);
    const setContextMenuVisibilityItemsAtom = useSetAtom(contextMenuVisibilityItemsAtom);
    const setToolBoxVisibility = useSetAtom(toolBoxVisibility);
    const setMoreToolVisibility = useSetAtom(moreToolVisibility);
    const setStickerToolMenuVisibility = useSetAtom(stickerToolMenuVisibility);
    const setSticketToolMenuColor = useSetAtom(stickerToolMenuColor);
    const setArrowToolMenuVisibility = useSetAtom(arrowToolMenuVisibility);
    const setArrowToolMenuLineType = useSetAtom(arrowToolMenuLineType);
    const setEmoteToolMenuVisibility = useSetAtom(emoteToolMenuAtomVisibility);
    const setEmoteToolMenuActiveChange = useSetAtom(emoteToolMenuAtomActive);
    const setMindMapToolMenuVisibility = useSetAtom(mindMapToolMenuVisibilityAtom);
    const setMindMapToolMenuActivePreset = useSetAtom(mindMapToolMenuActivePresetAtom);
    const setPenToolMenuVisibility = useSetAtom(penToolMenuVisibilityAtom);
    const setPenToolMenuType = useSetAtom(penToolMenuTypeAtom);
    const setPenToolMenuColor = useSetAtom(penToolMenuColorAtom);
    const setPenToolMenuLineWidth = useSetAtom(penToolMenuLineWidthAtom);
    const setToolMenuVisibility = useSetAtom(templateToolMenuVisibility);
    const setTimer = useSetAtom(timerAtom);
    const setMinimapUpdate = useSetAtom(minimapUpdateAtom);
    const setPresentationScreenVisibility = useSetAtom(presentationScreenVisibility);
    const setPresentationUID = useSetAtom(presentationUID);
    const setLinkInputCoordinates = useSetAtom(linkInputCoordinatesAtom);
    const setLinkInputDefaultValue = useSetAtom(linkInputDefaultValueAtom);
    const setLinkPopupState = useSetAtom(linkPopupStateAtom);

    useEffect(() => {
        const handleZoomUpdate: EventHandler<"canvas.zoom.update"> = ({ zoom }) => {
            setZoom(zoom);
        };
        const handleCurrentToolChange: EventHandler<"toolbox.currentTool"> = ({ tool }) => {
            setCurrentTool(tool);
        };
        const handleSearchResultsUpdate: EventHandler<"search.results.update"> = (data) => {
            setSearchResults(data);
        };
        const handleBoardInfoChange: EventHandler<"board.info"> = (boardInfo) => {
            setBoardInfo(boardInfo);
        };
        const handleMultiCursorsChange: EventHandler<"multi.cursors"> = ({
            disableMultiCursors,
        }) => {
            setMultiCursors({
                disableMultiCursors,
            });
        };
        const handleButtonDisabled: EventHandler<"button.disabled"> = (buttonStates) => {
            setButtonDisabled((prev) => ({
                ...prev,
                ...buttonStates,
            }));
        };
        const handleUserSessionsChange: EventHandler<"multi.users.sessions"> = ({ users }) => {
            setUsers((prev) => ({
                ...prev,
                users: users,
            }));
        };
        const handleUserTrackingSessionsChange: EventHandler<"multi.users.trackingSession"> = ({
            session,
        }) => {
            setUsers((prev) => {
                if (prev.trackingSession === session) {
                    return prev;
                }

                return { ...prev, trackingSession: session };
            });
        };
        const handleVoteTimerVisibilityChange: EventHandler<"vote.timer.visibility"> = ({
            isVisible,
        }) => {
            setVoteTimerVisibility(isVisible);
        };
        const handleHeaderActiveButtonNameChange: EventHandler<"header.button.active.name"> = ({
            name,
        }) => {
            setHeaderActiveButtonName(name);
        };
        const handlestartVoteVisibilityChange: EventHandler<"vote.start.visibility"> = ({
            isVisible,
        }) => {
            setStartVoteVisibility(isVisible);
        };
        const handleStartVoteActiveTabChange: EventHandler<"vote.start.activeTab"> = ({ tab }) => {
            setStartVoteActiveTab(tab);
        };
        const handleActiveVoteVisibilityChange: EventHandler<"vote.active.visibility"> = ({
            isVisible,
        }) => {
            setActiveVoteVisibility(isVisible);
        };
        const handleWidgetMenuCoordinatesChange: EventHandler<"menu.widget.coordinates"> = ({
            coordinates,
        }) => {
            setWidgetMenuCoordinates((prev) => {
                if (!prev) {
                    return coordinates;
                }

                if (prev.canvasMatrixKey !== coordinates?.canvasMatrixKey) {
                    return coordinates;
                }

                if (prev.top === coordinates?.top && prev.left === coordinates.left) {
                    return prev;
                }

                return coordinates;
            });
        };
        const handleWidgetMenuAvailableOptionsChange: EventHandler<
            "menu.widget.availableOptions"
        > = ({ options }) => {
            setWidgetMenuAvailableOptions(options);
        };
        const handleWidgetMenuOptionsChange: EventHandler<"menu.widget.options"> = ({
            options,
        }) => {
            setWidgetMenuOptions(options);
        };
        const handleContextMenuVisibilityChange: EventHandler<"contextMenu.visibility"> = (
            data,
        ) => {
            setContextMenuVisibility((prev) => {
                if (prev.isVisible === data.isVisible) {
                    return prev;
                }

                return data;
            });
        };
        const handleContextMenuVisibilityItemsAtomChange: EventHandler<
            "contextMenu.visibilityMenuItems"
        > = (value) => {
            setContextMenuVisibilityItemsAtom(value);
        };
        const handleToolBoxVisibilityChange: EventHandler<"toolBox.visibility"> = ({
            isVisible,
        }) => {
            setToolBoxVisibility(isVisible);
        };
        const handleMoreToolVisibilityChange: EventHandler<"moreTool.visibility"> = ({
            isVisible,
        }) => {
            setMoreToolVisibility(isVisible);
        };
        const handleSticketToolMenuVisibilityChange: EventHandler<"stickerToolMenu.visibility"> = ({
            isVisible,
        }) => {
            setStickerToolMenuVisibility(isVisible);
        };
        const handleStickerToolMenuColorChange: EventHandler<"stickerToolMenu.color"> = ({
            color,
        }) => {
            setSticketToolMenuColor(color);
        };
        const handleArrowToolMenuVisibilityChange: EventHandler<"arrowToolMenu.visibility"> = ({
            isVisible,
        }) => {
            setArrowToolMenuVisibility(isVisible);
        };
        const handleArrowToolMenuLineTypeChange: EventHandler<"arrowToolMenu.type"> = ({
            lineType,
        }) => {
            setArrowToolMenuLineType(lineType);
        };
        const handleEmoteToolMenuVisibilityChange: EventHandler<"emoteToolMenu.visibility"> = ({
            isVisible,
        }) => {
            setEmoteToolMenuVisibility(isVisible);
        };
        const handleEmoteToolMenuActiveChange: EventHandler<"emoteToolMenu.active"> = ({
            activeEmote,
        }) => {
            setEmoteToolMenuActiveChange(activeEmote);
        };
        const handleMindMapToolMenuVisibilityChange: EventHandler<"mindMapToolMenu.visibility"> = ({
            isVisible,
        }) => {
            setMindMapToolMenuVisibility(isVisible);
        };
        const handleMindMapToolMenuActivePresetChange: EventHandler<
            "mindMapToolMenu.activePreset"
        > = ({ preset }) => {
            setMindMapToolMenuActivePreset(preset);
        };

        const handlePenToolMenuVisibilityChange: EventHandler<"penToolMenu.visibility"> = ({
            isVisible,
        }) => {
            setPenToolMenuVisibility(isVisible);
        };
        const handlePenToolMenuTypeChange: EventHandler<"penToolMenu.penType"> = ({ penType }) => {
            setPenToolMenuType(penType);
        };
        const handlePenToolMenuColorChange: EventHandler<"penToolMenu.color"> = ({ color }) => {
            setPenToolMenuColor(color);
        };
        const handlePenToolMenuLineWidthChange: EventHandler<"penToolMenu.lineWidth"> = ({
            lineWidth,
        }) => {
            setPenToolMenuLineWidth(lineWidth);
        };
        const handleTemplateToolMenuVisibilityChange: EventHandler<
            "templateToolMenu.visibility"
        > = ({ isVisible }) => {
            setToolMenuVisibility(isVisible);
        };

        const handleTimerUpdate: EventHandler<"timer.update"> = (data) => {
            setTimer(data);
        };

        const handleMinimapUpdate: EventHandler<"minimap.update"> = ({ needUpdate }) => {
            setMinimapUpdate(needUpdate);
        };
        const handlePresentationScreenVisibilityChange: EventHandler<
            "presentation.mode.visibility"
        > = ({ isVisible }) => {
            setPresentationScreenVisibility(isVisible);
        };
        const handlePresentationUIDChange: EventHandler<"presentation.mode.presentationUID"> = ({
            UID,
        }) => {
            setPresentationUID(UID);
        };

        const handleLinkInputCoordinatesChange: EventHandler<"link.input.coordinates"> = ({
            coordinates,
        }) => {
            setLinkInputCoordinates(coordinates);
        };

        const handleLinkInputDefaultValueChange: EventHandler<"link.input.defaultValue"> = ({
            value,
        }) => {
            setLinkInputDefaultValue(value);
        };

        const handleLinkPopupStateChange: EventHandler<"link.popup.state"> = ({ state }) => {
            setLinkPopupState((prev) => {
                if (
                    prev?.url === state?.url &&
                    prev?.left === state?.left &&
                    prev?.top === state?.top
                ) {
                    return prev;
                }

                return state;
            });
        };

        eventEmitter.on("canvas.zoom.update", handleZoomUpdate);
        eventEmitter.on("search.results.update", handleSearchResultsUpdate);
        eventEmitter.on("board.info", handleBoardInfoChange);
        eventEmitter.on("multi.cursors", handleMultiCursorsChange);
        eventEmitter.on("button.disabled", handleButtonDisabled);
        eventEmitter.on("multi.users.sessions", handleUserSessionsChange);
        eventEmitter.on("multi.users.trackingSession", handleUserTrackingSessionsChange);
        eventEmitter.on("vote.timer.visibility", handleVoteTimerVisibilityChange);
        eventEmitter.on("header.button.active.name", handleHeaderActiveButtonNameChange);
        eventEmitter.on("vote.start.visibility", handlestartVoteVisibilityChange);
        eventEmitter.on("vote.start.activeTab", handleStartVoteActiveTabChange);
        eventEmitter.on("vote.active.visibility", handleActiveVoteVisibilityChange);
        eventEmitter.on("menu.widget.coordinates", handleWidgetMenuCoordinatesChange);
        eventEmitter.on("menu.widget.availableOptions", handleWidgetMenuAvailableOptionsChange);
        eventEmitter.on("menu.widget.options", handleWidgetMenuOptionsChange);
        eventEmitter.on("contextMenu.visibility", handleContextMenuVisibilityChange);
        eventEmitter.on(
            "contextMenu.visibilityMenuItems",
            handleContextMenuVisibilityItemsAtomChange,
        );
        eventEmitter.on("toolBox.visibility", handleToolBoxVisibilityChange);
        eventEmitter.on("toolbox.currentTool", handleCurrentToolChange);
        eventEmitter.on("moreTool.visibility", handleMoreToolVisibilityChange);
        eventEmitter.on("stickerToolMenu.visibility", handleSticketToolMenuVisibilityChange);
        eventEmitter.on("stickerToolMenu.color", handleStickerToolMenuColorChange);
        eventEmitter.on("arrowToolMenu.visibility", handleArrowToolMenuVisibilityChange);
        eventEmitter.on("arrowToolMenu.type", handleArrowToolMenuLineTypeChange);
        eventEmitter.on("emoteToolMenu.visibility", handleEmoteToolMenuVisibilityChange);
        eventEmitter.on("emoteToolMenu.active", handleEmoteToolMenuActiveChange);
        eventEmitter.on("mindMapToolMenu.visibility", handleMindMapToolMenuVisibilityChange);
        eventEmitter.on("mindMapToolMenu.activePreset", handleMindMapToolMenuActivePresetChange);
        eventEmitter.on("penToolMenu.visibility", handlePenToolMenuVisibilityChange);
        eventEmitter.on("penToolMenu.penType", handlePenToolMenuTypeChange);
        eventEmitter.on("penToolMenu.color", handlePenToolMenuColorChange);
        eventEmitter.on("penToolMenu.lineWidth", handlePenToolMenuLineWidthChange);
        eventEmitter.on("templateToolMenu.visibility", handleTemplateToolMenuVisibilityChange);
        eventEmitter.on("timer.update", handleTimerUpdate);
        eventEmitter.on("minimap.update", handleMinimapUpdate);
        eventEmitter.on("presentation.mode.visibility", handlePresentationScreenVisibilityChange);
        eventEmitter.on("presentation.mode.presentationUID", handlePresentationUIDChange);
        eventEmitter.on("link.input.coordinates", handleLinkInputCoordinatesChange);
        eventEmitter.on("link.input.defaultValue", handleLinkInputDefaultValueChange);
        eventEmitter.on("link.popup.state", handleLinkPopupStateChange);

        setBoardInfo(canv.initData as InitialData);
        setMultiCursors({
            disableMultiCursors: canv.multiManager.disableMultiCursors,
        });

        return () => {
            eventEmitter.off("canvas.zoom.update", handleZoomUpdate);
            eventEmitter.off("search.results.update", handleSearchResultsUpdate);
            eventEmitter.off("board.info", handleBoardInfoChange);
            eventEmitter.off("multi.cursors", handleMultiCursorsChange);
            eventEmitter.off("button.disabled", handleButtonDisabled);
            eventEmitter.off("multi.users.sessions", handleUserSessionsChange);
            eventEmitter.off("multi.users.trackingSession", handleUserTrackingSessionsChange);
            eventEmitter.off("vote.timer.visibility", handleVoteTimerVisibilityChange);
            eventEmitter.off("header.button.active.name", handleHeaderActiveButtonNameChange);
            eventEmitter.off("vote.start.visibility", handlestartVoteVisibilityChange);
            eventEmitter.off("vote.start.activeTab", handleStartVoteActiveTabChange);
            eventEmitter.off("vote.active.visibility", handleActiveVoteVisibilityChange);
            eventEmitter.off("menu.widget.coordinates", handleWidgetMenuCoordinatesChange);
            eventEmitter.off(
                "menu.widget.availableOptions",
                handleWidgetMenuAvailableOptionsChange,
            );
            eventEmitter.off("contextMenu.visibility", handleContextMenuVisibilityChange);
            eventEmitter.off(
                "contextMenu.visibilityMenuItems",
                handleContextMenuVisibilityItemsAtomChange,
            );
            eventEmitter.off("toolBox.visibility", handleToolBoxVisibilityChange);
            eventEmitter.off("toolbox.currentTool", handleCurrentToolChange);
            eventEmitter.off("moreTool.visibility", handleMoreToolVisibilityChange);
            eventEmitter.off("stickerToolMenu.visibility", handleSticketToolMenuVisibilityChange);
            eventEmitter.off("stickerToolMenu.color", handleStickerToolMenuColorChange);
            eventEmitter.off("arrowToolMenu.visibility", handleArrowToolMenuVisibilityChange);
            eventEmitter.off("arrowToolMenu.type", handleArrowToolMenuLineTypeChange);
            eventEmitter.off("emoteToolMenu.visibility", handleEmoteToolMenuVisibilityChange);
            eventEmitter.off("emoteToolMenu.active", handleEmoteToolMenuActiveChange);
            eventEmitter.off("mindMapToolMenu.visibility", handleMindMapToolMenuVisibilityChange);
            eventEmitter.off(
                "mindMapToolMenu.activePreset",
                handleMindMapToolMenuActivePresetChange,
            );
            eventEmitter.off("penToolMenu.visibility", handlePenToolMenuVisibilityChange);
            eventEmitter.off("penToolMenu.penType", handlePenToolMenuTypeChange);
            eventEmitter.off("penToolMenu.color", handlePenToolMenuColorChange);
            eventEmitter.off("penToolMenu.lineWidth", handlePenToolMenuLineWidthChange);
            eventEmitter.off("templateToolMenu.visibility", handleTemplateToolMenuVisibilityChange);
            eventEmitter.off("timer.update", handleTimerUpdate);
            eventEmitter.off("minimap.update", handleMinimapUpdate);
            eventEmitter.off(
                "presentation.mode.visibility",
                handlePresentationScreenVisibilityChange,
            );
            eventEmitter.off("presentation.mode.presentationUID", handlePresentationUIDChange);

            eventEmitter.off("link.input.coordinates", handleLinkInputCoordinatesChange);
            eventEmitter.off("link.input.defaultValue", handleLinkInputDefaultValueChange);
            eventEmitter.off("link.popup.state", handleLinkPopupStateChange);
            canv.destroy();
        };
    }, [
        setZoom,
        setCurrentTool,
        setSearchResults,
        setBoardInfo,
        setMultiCursors,
        setButtonDisabled,
        setUsers,
        setVoteTimerVisibility,
        setHeaderActiveButtonName,
        setStartVoteVisibility,
        setStartVoteActiveTab,
        setActiveVoteVisibility,
        setWidgetMenuCoordinates,
        setWidgetMenuAvailableOptions,
        setWidgetMenuOptions,
        setContextMenuVisibility,
        setContextMenuVisibilityItemsAtom,
        setToolBoxVisibility,
        setMoreToolVisibility,
        setStickerToolMenuVisibility,
        setSticketToolMenuColor,
        setArrowToolMenuVisibility,
        setArrowToolMenuLineType,
        setEmoteToolMenuVisibility,
        setEmoteToolMenuActiveChange,
        setMindMapToolMenuVisibility,
        setMindMapToolMenuActivePreset,
        setPenToolMenuVisibility,
        setPenToolMenuType,
        setPenToolMenuColor,
        setPenToolMenuLineWidth,
        setToolMenuVisibility,
        setTimer,
        setMinimapUpdate,
        setPresentationScreenVisibility,
        setPresentationUID,
        setLinkInputCoordinates,
        setLinkInputDefaultValue,
        setLinkPopupState,
    ]);

    return (
        <ThemeProvider theme="light">
            <NiceModal.Provider>
                <HeaderBar />
                <SideBar />
                <RightSheet />
                <BottomBar />
                <WidgetMenu />
                <ContextMenu />
                <FullscreenPresentationLayout />
                <LinkInput />
                <LinkPopup />
            </NiceModal.Provider>
        </ThemeProvider>
    );
};
